import { useEffect } from 'react'
import type { Conn } from '../live'
import type { UserView } from '../api'
import { t } from '../i18n'

/*
 * The Strevix logo of the cabinet and the console (design/brand, BR1 S1 "Core · Orbit"),
 * as a state logo (BR1 report, 16.10 (4) and (5)):
 *   on air      — the stream is on air (the sender decides: traffic from OBS): the loop
 *   connecting  — live data is connecting or reconnecting
 *   waiting     — Studio / Status before the stream: waiting for the signal from OBS
 *   static      — everything else (admin pages, apps, no stream)
 * The files are web/public/brand/, byte copies of the design system written by scripts/ds-sync.sh
 * (`make ds-check` in CI): the static lockup and mark from assets/Logo, the state logos from
 * code/state-logos (AUDIT SYS-10, SYS-13). The animation is CSS inside the SVG and stops under
 * prefers-reduced-motion by itself. The state still reads from the badges of the page: the logo
 * repeats it, it does not replace it.
 * The static mark keeps the system's crop (01-logo): its box is tighter than the animated marks'
 * (their arcs need room to fire), so the mark reads a little larger when it is still — the same
 * as the horizontal lockup, whose static and animated files also differ in scale.
 */

export type BrandState = 'static' | 'onair' | 'connecting' | 'waiting'

const FILE: Record<BrandState, string> = { static: '', onair: '-loop', connecting: '-connecting', waiting: '-waiting' }
const LABEL: Record<BrandState, string> = { static: 'Strevix', onair: 'Strevix — on air', connecting: 'Strevix — connecting', waiting: 'Strevix — waiting for the stream' }

/** dark | light: the theme is set once at start (main.tsx, ?theme=light). */
const theme = () => (document.documentElement.dataset.theme === 'light' ? 'light' : 'dark')

export function brandState({ view, conn, liveScreen }: { view: UserView | null; conn: Conn; liveScreen: boolean }): BrandState {
  if (view && view.node !== 'unreachable' && view.inputs.some((i) => i.live)) return 'onair'
  if (!view) return 'static'
  if (conn !== 'open') return 'connecting'
  return liveScreen ? 'waiting' : 'static'
}

/** Header logo: the full logo from 768 px (bp-md), the mark alone on a phone (the header buttons fit one row). */
export function BrandLogo({ state }: { state: BrandState }) {
  const th = theme()
  const f = FILE[state]
  return (
    <span className="flex items-center" data-testid="brand-logo" data-state={state} role="img" aria-label={LABEL[state]}>
      <img src={`/brand/horizontal${f}-${th}.svg`} alt="" width={86} height={28} className="block h-7 w-auto max-md:hidden" draggable={false} />
      <img src={`/brand/mark${f}-${th}.svg`} alt="" width={26} height={28} className="block h-7 w-auto md:hidden" draggable={false} />
    </span>
  )
}

/**
 * The browser tab while the stream is on air (01-logo «App, tray and favicon», F-14): the On Air
 * favicon and the title «● Live · Strevix»; off air the plain favicon and the title the page had.
 */
export function useBrandTab(state: BrandState) {
  const onair = state === 'onair'
  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    if (!link) return
    link.href = onair ? '/brand/favicon-onair.svg' : '/favicon.svg'
  }, [onair])
  useEffect(() => {
    if (!onair) return
    const before = document.title
    const live = t('brand.tabLive')
    document.title = live
    // back to the page's own title, unless something else has set one meanwhile
    return () => {
      if (document.title === live) document.title = before
    }
  }, [onair])
}

/**
 * The console's lockup for its 48 px top bar (11-console «logo + CONTROL label»; drawn in the
 * system's ScreenNetworkMap header): the static horizontal logo, 26 px, then «Control» as an
 * .sx-label after a line-2 rule. The console shows no broadcast, so the logo never takes a state.
 * «Control» is the console's name, like a product name: not translated (AUDIT RD13-01).
 * Phone: the mark alone (01-logo: the full mark from 24 px), the label stays.
 */
export function ConsoleBrand() {
  const th = theme()
  return (
    <span className="flex items-center gap-5" data-testid="console-brand">
      <span className="flex items-center" role="img" aria-label="Strevix">
        <img src={`/brand/horizontal-${th}.svg`} alt="" width={80} height={26} className="block h-[26px] w-auto max-md:hidden" draggable={false} />
        <img src={`/brand/mark-${th}.svg`} alt="" width={24} height={26} className="block h-[26px] w-auto md:hidden" draggable={false} />
      </span>
      <span className="sx-label border-l border-line-2 pl-3 text-text-soft">Control</span>
    </span>
  )
}
